/**
 * RecentActivityWidget Component - T143
 *
 * Widget showing recent invoice activity
 */
import { Link } from '@tanstack/react-router';
import { GlassPanel } from '../ui';
import { formatCurrency, formatDate } from '@armard/shared';
import { useRecentActivity, activityTypeLabels } from '../../hooks/useDashboard';

interface RecentActivityWidgetProps {
  limit?: number;
  className?: string;
}

const activityIcons: Record<string, React.ReactNode> = {
  invoice_created: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  invoice_sent: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  invoice_paid: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  customer_created: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
    </svg>
  ),
};

const activityColors: Record<string, string> = {
  invoice_created: 'bg-blue-500/20 text-blue-400',
  invoice_sent: 'bg-purple-500/20 text-purple-400',
  invoice_paid: 'bg-green-500/20 text-green-400',
  customer_created: 'bg-cyan-500/20 text-cyan-400',
};

export function RecentActivityWidget({ limit = 5, className = '' }: RecentActivityWidgetProps) {
  const { data, isLoading, error } = useRecentActivity(limit);

  if (isLoading) {
    return (
      <GlassPanel className={`p-4 ${className}`}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-white/10 rounded w-1/3" />
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-white/5 rounded" />
            ))}
          </div>
        </div>
      </GlassPanel>
    );
  }

  if (error) {
    return (
      <GlassPanel className={`p-4 ${className}`}>
        <p className="text-red-400 text-center py-4">Fehler beim Laden</p>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel className={`p-4 ${className}`}>
      <h3 className="text-lg font-medium text-white mb-4">Letzte Aktivitäten</h3>

      {data && data.length > 0 ? (
        <div className="space-y-3">
          {data.map((activity) => (
            <Link
              key={activity.id}
              to="/invoices/$id"
              params={{ id: activity.entityId }}
              className="flex items-start gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <div className={`p-2 rounded-lg ${activityColors[activity.type]}`}>
                {activityIcons[activity.type]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{activity.entityName}</p>
                <p className="text-xs text-white/40">
                  {activityTypeLabels[activity.type]}
                  {activity.amount && ` · ${formatCurrency(activity.amount)}`}
                </p>
              </div>
              <span className="text-xs text-white/30">{formatDate(activity.timestamp)}</span>
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-white/40 text-center py-4">Keine Aktivitäten</p>
      )}
    </GlassPanel>
  );
}
